import React from 'react';

import { useIntl } from 'react-intl';
import { Select } from 'antd';
import type { Moment } from 'moment';

import { ALL_OPTION_VALUE } from '@src/components/BaseSelector';
import Title from '@src/components/BoxContainer/components/Title';
import type {
  FilterValues,
  SetValueProps,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import { KeyValueWidget } from '@src/components/KeyValueWidget';
import RelativeAbsolutePeriodFilter, {
  type RelativeAbsolutePeriodValue,
} from '@src/components/RelativeAbsolutePeriodFilter';
import { ScheduleTemplateSelector } from '@src/containers/ScheduleTemplateSelector';
import CostCenterFilterSelector from '@src/packages/costCenters/components/CostCenterFilterSelector';
import { DateUtil } from '@src/packages/utils/dates';
import { REMOTE_CONFIG_KEYS } from '@src/packages/utils/hooks/remoteConfigs/remoteConfigKeys';
import { useRemoteConfigAccess } from '@src/packages/utils/hooks/remoteConfigs/useRemoteConfigAccess';
import IntlMessages from '@src/util/IntlMessages';

import { dateTypes } from '../../../SchedulesDashboard/constants';
import {
  buildTaskFilterSetValueProps,
  DEFAULT_DATE_RANGE,
  DEFAULT_SELECTED_TEMPLATES,
  EMPTY_SELECTED_TEMPLATES,
  filterValuesToTaskFiltersUi,
  periodValueToTaskFilterProps,
  TASKS_DASHBOARD_FILTER_FIELDS,
  TASKS_REPORT_MAX_SPAN,
  taskFiltersToPeriodValue,
  type TasksChartFilters,
} from '../../tasksFilterConfig';
import type { ScheduleDashboardDateType, SelectedSchedulesState } from '../../types';

const { Option } = Select;

const DEFAULT_ABSOLUTE_RANGE = (): [Moment, Moment] => [...DEFAULT_DATE_RANGE];

interface TasksDashboardFiltersProps {
  handleFilters: (props: SetValueProps | SetValueProps[]) => void;
  filters: FilterValues;
  chartFilters: TasksChartFilters;
  orgId: string;
  /** Same scope the dashboard queries: `[]` means "all" for org-wide roles. */
  divisionIds: string[];
  disabledDate: (date: Moment) => boolean;
  onTemplatesChange: (templates: SelectedSchedulesState) => void;
}

const TasksDashboardFilters: React.FC<TasksDashboardFiltersProps> = ({
  handleFilters,
  filters,
  chartFilters,
  orgId,
  divisionIds,
  onTemplatesChange,
}) => {
  const intl = useIntl();
  const { isAllowed: costCentersAllowed } = useRemoteConfigAccess(REMOTE_CONFIG_KEYS.COST_CENTERS);
  const uiFilters = filterValuesToTaskFiltersUi(filters, chartFilters);
  const { groupBy, templates, costCenterIds } = uiFilters;
  const periodValue = taskFiltersToPeriodValue(uiFilters);

  const setFilterValue = (key: string, value: unknown) => {
    handleFilters(buildTaskFilterSetValueProps(key, value, intl));
  };

  const handlePeriodChange = (next: RelativeAbsolutePeriodValue) => {
    handleFilters(periodValueToTaskFilterProps(next, intl));
  };

  const handleGroupByChange = (value: ScheduleDashboardDateType) => {
    setFilterValue(TASKS_DASHBOARD_FILTER_FIELDS.GROUP_BY, value);
  };

  const handleCostCentersChange = (next: string[]) => {
    setFilterValue(TASKS_DASHBOARD_FILTER_FIELDS.COST_CENTERS, next);
  };

  const handleTemplatesChange = (ids: string[]) => {
    if (ids?.includes(ALL_OPTION_VALUE)) {
      onTemplatesChange(DEFAULT_SELECTED_TEMPLATES);
      return;
    }

    // Clicking the active "all" option (or clearing) unselects everything.
    if (!ids?.length) {
      onTemplatesChange(EMPTY_SELECTED_TEMPLATES);
      return;
    }

    const byId = ids.reduce<SelectedSchedulesState['byId']>((acc, id) => {
      acc[id] = templates.byId[id] ?? { id };
      return acc;
    }, {});

    onTemplatesChange({ byId, selectedAll: false });
  };

  return (
    <div className="gx-flex-column gx-guarnic-gap-3">
      <RelativeAbsolutePeriodFilter
        historicalOnly
        value={periodValue}
        onChange={handlePeriodChange}
        label={
          <span className="gx-guarnic-title-filter">
            <IntlMessages id="dashboards.schedules.dateRange" />
          </span>
        }
        allowClear={false}
        defaultAbsoluteRange={DEFAULT_ABSOLUTE_RANGE}
        maxSpan={TASKS_REPORT_MAX_SPAN}
        showPresets
        timeZone={DateUtil.getDefaultTimeZone()}
      />
      <KeyValueWidget
        label={<Title.Filter value={<IntlMessages id="dashboards.schedules.groupResultsBy" />} />}
        value={
          <Select<ScheduleDashboardDateType>
            id="groupBy"
            onChange={handleGroupByChange}
            value={groupBy}
            dropdownMatchSelectWidth={false}
          >
            {dateTypes.map(d => (
              <Option key={d} value={d}>
                <IntlMessages id={`dateTypes.${d}`} values={{ amount: 1 }} />
              </Option>
            ))}
          </Select>
        }
      />
      <KeyValueWidget
        label={<Title.Filter value={<IntlMessages id="schedules.type.task" />} />}
        value={
          <ScheduleTemplateSelector
            organizationId={orgId}
            divisionIds={divisionIds}
            value={templates.selectedAll ? [ALL_OPTION_VALUE] : Object.keys(templates.byId)}
            onChange={handleTemplatesChange}
            templateIds={templates.selectedAll ? [] : Object.keys(templates.byId)}
            exactMatch={false}
          />
        }
      />
      {costCentersAllowed ? (
        <KeyValueWidget
          label={<Title.Filter value={<IntlMessages id="costCenters.filter.label" />} />}
          value={
            <CostCenterFilterSelector
              value={costCenterIds}
              onChange={handleCostCentersChange}
              forceVisible
            />
          }
        />
      ) : null}
    </div>
  );
};

export default React.memo(TasksDashboardFilters);
